// Footer newsletter → Klaviyo client-side subscribe (email + SMS consent).
// Reads company_id (public key) + list_id from the element's data attributes.
const KLAVIYO_REVISION = '2025-07-15';

if (!customElements.get('footer-newsletter')) {
  customElements.define(
    'footer-newsletter',
    class FooterNewsletter extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.fields = this.querySelector('.footer-newsletter__fields');
        this.emailInput = this.querySelector('input[type="email"]');
        this.nameInput = this.querySelector('input[name="first_name"]');
        this.phoneInput = this.querySelector('input[type="tel"]');
        this.button = this.querySelector('button[type="submit"]');
        this.errorEl = this.querySelector('[data-error]');
        this.successEl = this.querySelector('[data-success]');

        this.companyId = this.dataset.companyId;
        this.listId = this.dataset.listId;
        this.defaultCountry = (this.dataset.defaultCountry || 'dk').toLowerCase();

        // Translated, customer-facing messages from the Liquid snippet (English fallbacks).
        this.messages = {
          name: this.dataset.errorName || 'Please enter your name.',
          email: this.dataset.errorEmail || 'Please enter a valid email.',
          phone: this.dataset.errorPhone || 'Please enter your phone number.',
          phoneInvalid: this.dataset.errorPhoneInvalid || 'Please enter a valid phone number.',
          generic: this.dataset.errorGeneric || 'Something went wrong. Please try again.',
        };

        if (this.phoneInput) this.initPhone();
        if (this.form) this.form.addEventListener('submit', (e) => this.handleSubmit(e));
      }

      initPhone() {
        if (!window.intlTelInput) return;
        this.iti = window.intlTelInput(this.phoneInput, {
          initialCountry: this.defaultCountry,
          strictMode: true,
        });
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.style.display = 'block';
      }

      // Subscribe a profile to one Klaviyo list, setting the requested consent(s).
      subscribe(listId, { email, phone, firstName, emailConsent, smsConsent }) {
        const attributes = { email };
        if (firstName) attributes.first_name = firstName;
        if (phone) attributes.phone_number = phone;

        const subscriptions = {};
        if (emailConsent) subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
        if (smsConsent && phone) subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
        if (Object.keys(subscriptions).length) attributes.subscriptions = subscriptions;

        const payload = {
          data: {
            type: 'subscription',
            attributes: { profile: { data: { type: 'profile', attributes } } },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        };

        return fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
            body: JSON.stringify(payload),
          }
        );
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (this.errorEl) this.errorEl.style.display = 'none';

        if (!this.companyId || !this.listId) {
          // Merchant misconfiguration — log for us, show the customer a neutral message.
          console.error('[footer-newsletter] Missing Klaviyo company_id or list_id.');
          this.showError(this.messages.generic);
          return;
        }

        const firstName = this.nameInput ? this.nameInput.value.trim() : '';
        if (this.nameInput && !firstName) {
          this.showError(this.messages.name);
          return;
        }

        const email = this.emailInput.value.trim();
        if (!email || !this.emailInput.checkValidity()) {
          this.showError(this.messages.email);
          return;
        }

        let phone = '';
        if (this.phoneInput) {
          // Wait for intl-tel-input utils (async) so validation/E.164 is ready.
          if (this.iti && this.iti.promise) {
            try { await this.iti.promise; } catch (e) { /* utils failed to load */ }
          }
          if (!this.phoneInput.value.trim()) {
            this.showError(this.messages.phone);
            return;
          }
          if (this.iti && !this.iti.isValidNumber()) {
            this.showError(this.messages.phoneInvalid);
            return;
          }
          phone = this.iti ? this.iti.getNumber() : this.phoneInput.value.trim();
        }

        if (this.button) this.button.disabled = true;

        try {
          // One list captures both email and SMS consent (consent is profile-level).
          const response = await this.subscribe(this.listId, {
            email,
            phone,
            firstName,
            emailConsent: true,
            smsConsent: !!phone,
          });

          if (response.ok) {
            if (this.fields) this.fields.style.display = 'none';
            if (this.successEl) {
              this.successEl.style.display = 'block';
              this.successEl.focus();
            }
          } else {
            const data = await response.json().catch(() => null);
            console.error('[footer-newsletter] Klaviyo error', response.status, data);
            this.showError(this.messages.generic);
            if (this.button) this.button.disabled = false;
          }
        } catch (error) {
          console.error('[footer-newsletter]', error);
          this.showError('Something went wrong. Please try again.');
          if (this.button) this.button.disabled = false;
        }
      }
    }
  );
}
